import type { Progress, Wardrobe } from './save';
import { WEARABLE_ORDER } from '../economy/wardrobe';

export interface Task {
  id: string;
  label: string;
  done: boolean;
}

const HUNTS_TARGET = 5;

/**
 * Wave I (PLAN-CITY3 §5, I-4: "meta/tasks.ts — a pure VIEW over flags that
 * already exist"). Every flag read here is already durable, one-time-forever
 * save state some earlier wave built (S10/S11/L-1/L-2/L-3/Wave D) — this
 * file invents NO new progress state of its own; it only turns those flags
 * into one ordered checklist. 11 real tasks on day one (3 stashes, 3 dens, 2
 * crafts, a 5-hunts bar, the golden pigeon, all-9-fits) — deliberately NOT a
 * second source of truth for any of them (Classic gotcha-style: never
 * re-derive a value two places), so a future wave that adds a new one-time
 * flag just needs one more line here, not a parallel tracking structure.
 *
 * The reusable "persistent collectible" primitive (loot/pickup.ts's
 * `spawnPersistentCollectible`, `Progress.placedCollectibles`) deliberately
 * has NO row in this list yet — nothing has been placed by any wave, so
 * there is nothing real to count. `collectiblesFoundCount` below is exported
 * for whichever future wave places the first one to build its own task row
 * from, once it actually has ids to pass.
 */
export function buildTasks(progress: Progress, wardrobe: Wardrobe): Task[] {
  return [
    { id: 'stash_hidden', label: 'Find the hidden stash', done: progress.hiddenStashFound },
    { id: 'stash_laundromat', label: 'Check the laundromat machines', done: progress.laundromatStashFound },
    { id: 'stash_rooftop', label: 'Take in the rooftop view', done: progress.rooftopVantageFound },
    { id: 'den_gridlock', label: 'Defeat the Gridlock Golem', done: progress.gridlockLairCleared },
    { id: 'den_ratking', label: 'Defeat the Rat King', done: progress.ratKingLairCleared },
    { id: 'den_sewer', label: 'Clear the sewer den', done: progress.sewerDenCleared },
    { id: 'craft_armor', label: 'Craft Scrap Armor', done: progress.armorCrafted },
    { id: 'craft_plating', label: 'Craft Riot Plating', done: progress.platingCrafted },
    { id: 'hunts_five', label: `Complete ${HUNTS_TARGET} bodega hunts`, done: progress.huntsCompleted >= HUNTS_TARGET },
    { id: 'golden_pigeon', label: 'Find the Golden Pigeon', done: !!progress.everKilled['golden_pigeon'] },
    { id: 'wardrobe_full', label: 'Collect all 9 Street Fits', done: WEARABLE_ORDER.every((id) => !!wardrobe.owned[id]) },
  ];
}

/** The HUD pill's fallback source (main.ts) and the STASH screen's [OBJECTIVES] "up next" line — the first not-yet-done task in the fixed order above, or null once every task is done. */
export function nextUndoneTask(tasks: Task[]): Task | null {
  return tasks.find((t) => !t.done) ?? null;
}

/** For a future collectible-placing wave's own task row — see this file's own header comment on why no such row exists yet. */
export function collectiblesFoundCount(progress: Progress, knownIds: readonly string[]): number {
  return knownIds.filter((id) => !!progress.placedCollectibles[id]).length;
}
